import React, { useMemo } from 'react';
import {
  CategoryField,
  TextCollectionFieldType,
  CategoryChoice,
} from '@scale/shared/types/params/textcollection';

import { isNestedLabelChoice, LabelChoice } from '@scale/shared/types/params/annotation';
import { CategoryFieldAnnotation } from '@scale/shared/types/response/textcollection';
import CategoryFieldSection from 'react-scale/components/scalers/tasks/textcollection/CategoryFieldSection';
import { SuggestionsProvider } from 'react-scale/components/scalers/tasks/textcollection/suggestions';
import { getCategoryFullLabelPath } from '@scale/shared/textcollection/field';

// TODO: if future nested attributes are added, this will need to be changed
export const NESTED_ANNOTATION_ATTRIBUTE_PATH_DELIMITER = ' > ';

// function that takes a NestedLabelChoice and converts it to a CategoryChoice recursively
const convertLabelChoiceToCategoryChoice = (labelChoice: LabelChoice): CategoryChoice => {
  if (!isNestedLabelChoice(labelChoice)) {
    const categoryChoice: CategoryChoice = {
      // if its a string then assign direct else assign the choice
      label: typeof labelChoice === 'string' ? labelChoice : labelChoice.choice,
      value: typeof labelChoice === 'string' ? labelChoice : labelChoice.choice,
    };
    return categoryChoice;
  }

  const { subchoices, choice } = labelChoice;
  // end recursive calls if subchoices is empty/undefined
  const categoryChoice: CategoryChoice = {
    label: choice,
    value: choice,
    subchoices: [],
  };

  // if there are children, recursively convert them to CategoryChoices
  if (subchoices) {
    categoryChoice.subchoices = subchoices.map(subchoice => {
      return convertLabelChoiceToCategoryChoice(subchoice);
    });
  }
  return categoryChoice;
};

const convertAnnotationAttributeToCategoryField = (
  choices: LabelChoice[],
  isSingleSelect: boolean,
  id?: string,
): CategoryField => {
  // iterate through the attribute's choices and create an array of category fields
  // for each tree of nested attributes
  const categoryChoices: CategoryChoice[] = [];

  for (const choice of choices) {
    categoryChoices.push(convertLabelChoiceToCategoryChoice(choice));
  }

  const categoryField: CategoryField = {
    field_id: id || '',
    title: '',
    required: true,
    min_choices: 1,
    max_choices: isSingleSelect ? 1 : 100,
    can_select_intermediate_nodes: true,
    type: TextCollectionFieldType.Category,
    choices: categoryChoices,
    showSearchBar: false,
  };
  return categoryField;
};

// strings are words separated by sidewise carrots (>), get the last word in the string that is not the word Unknown also remove whitespace
const getLastWord = (choice: string): string | undefined => {
  const lastWord = choice
    .split(NESTED_ANNOTATION_ATTRIBUTE_PATH_DELIMITER)
    .map(word => word.trim())
    .pop();

  return lastWord;
};

const loadSelectedChoices = (selectedChoices?: string | string[] | undefined) => {
  const selectedChoicesArray: string[] = [];
  if (selectedChoices) {
    if (typeof selectedChoices === 'string') {
      const lastWord = getLastWord(selectedChoices);
      if (lastWord) selectedChoicesArray.push(lastWord);
    } else {
      for (const choice of selectedChoices) {
        const lastWord = getLastWord(choice);
        if (lastWord) selectedChoicesArray.push(lastWord);
      }
    }
  }
  return selectedChoicesArray;
};

interface CategoryFieldSectionWrapperProps {
  attrKey: string;
  id?: string;
  choices: LabelChoice[];
  handleMultiSelect: (choices: string[]) => void;
  handleUpdateSelection: (attrKey: string, attrVal: string | string[] | number | undefined) => void;
  selectedChoices?: string | string[] | undefined;
  isSingleSelect: boolean;
}

const CategoryFieldSectionWrapper: React.FC<CategoryFieldSectionWrapperProps> = ({
  attrKey,
  id,
  choices,
  handleMultiSelect,
  handleUpdateSelection,
  selectedChoices,
  isSingleSelect,
}) => {
  // convert categoryField to memo to prevent rerendering
  const categoryField = useMemo(
    () => convertAnnotationAttributeToCategoryField(choices, isSingleSelect),
    [choices, isSingleSelect],
  );

  return (
    <SuggestionsProvider>
      <CategoryFieldSection
        field={categoryField}
        annotation={{
          type: TextCollectionFieldType.Category,
          field_id: id || '',
          response: [loadSelectedChoices(selectedChoices)],
        }}
        setAnnotation={(r: CategoryFieldAnnotation) => {
          const labelChoiceResult: string[] = [];
          for (const choice of r.response?.[0]) {
            labelChoiceResult.push(getCategoryFullLabelPath(choice, categoryField));
          }

          isSingleSelect
            ? handleUpdateSelection(attrKey, labelChoiceResult[0])
            : handleMultiSelect(labelChoiceResult);
        }}
        currentAnnotations={{}}
      />
    </SuggestionsProvider>
  );
};

export default CategoryFieldSectionWrapper;
